import React, { useState, useEffect } from 'react';
import { getDrafts, sendDraft, deleteDraft } from '../../lib/api';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { formatDate } from '../../lib/utils';
import { toast } from 'sonner';
import { 
  FileEdit, Send, Trash2, RefreshCw, Mail, User, Eye, X, Check, Clock,
  Receipt, DollarSign, Paperclip, Download, FileText, Image, File,
  Calendar, Building, ExternalLink, Plus, Loader2
} from 'lucide-react';

function DraftsPage() {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [sending, setSending] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [showAttachments, setShowAttachments] = useState(false);
  
  // Compose new email
  const [showCompose, setShowCompose] = useState(false);
  const [composing, setComposing] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [newEmail, setNewEmail] = useState({
    to: '',
    subject: '',
    body: '',
    attachments: []
  });

  useEffect(function() { loadDrafts(); }, []);

  async function loadDrafts() {
    setLoading(true);
    try { 
      const response = await getDrafts();
      setDrafts(response.data || []);
    }
    catch(e) { 
      console.error(e); 
      toast.error('فشل تحميل المسودات');
    }
    finally { setLoading(false); }
  }

  async function handleAttachmentUpload(e) {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    setUploadingAttachment(true);
    
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`الملف ${file.name} كبير جداً (الحد الأقصى 10MB)`);
        continue;
      }
      
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await api.post('/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        if (response.data.file_path) {
          setNewEmail(prev => ({
            ...prev,
            attachments: [...prev.attachments, {
              name: file.name,
              path: response.data.file_path,
              size: file.size
            }]
          }));
          toast.success(`تم رفع ${file.name}`);
        }
      } catch (err) {
        console.error('Upload error:', err);
        toast.error(`فشل رفع ${file.name}`);
      }
    }
    
    setUploadingAttachment(false);
    e.target.value = '';
  }

  function removeAttachment(index) {
    setNewEmail(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  }

  async function handleSendNewEmail() {
    if (!newEmail.to || !newEmail.subject) {
      toast.error('يرجى إدخال المستلم والموضوع');
      return;
    }
    
    setComposing(true);
    try {
      const response = await api.post('/settings/email/send', {
        to: newEmail.to.split(',').map(e => e.trim()),
        subject: newEmail.subject,
        body_html: newEmail.body,
        attachments: newEmail.attachments.map(a => a.path)
      });
      
      if (response.data.success) {
        toast.success('تم إرسال البريد بنجاح! ✅');
        setShowCompose(false);
        setNewEmail({ to: '', subject: '', body: '', attachments: [] });
      } else {
        toast.error(response.data.message || 'فشل إرسال البريد');
      }
    } catch(e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'فشل إرسال البريد');
    } finally {
      setComposing(false);
    }
  }

  async function handleSend(draftId) {
    setSending(draftId);
    try {
      await sendDraft(draftId);
      toast.success('تم إرسال البريد بنجاح!');
      loadDrafts();
      setSelectedDraft(null);
    } catch(e) {
      toast.error(e.response?.data?.detail || 'فشل إرسال البريد');
    } finally {
      setSending(null);
    }
  }

  async function handleDelete(draftId) {
    if (!confirm('هل أنت متأكد من حذف هذه المسودة؟')) return;
    
    setDeleting(draftId);
    try {
      await deleteDraft(draftId);
      toast.success('تم حذف المسودة');
      loadDrafts();
      if (selectedDraft?.id === draftId) setSelectedDraft(null);
    } catch(e) {
      toast.error('فشل حذف المسودة');
    } finally {
      setDeleting(null);
    }
  }

  function getStatusBadge(status) {
    switch(status) {
      case 'pending': return <Badge variant="secondary"><Clock className="h-3 w-3 mr-1" />قيد المراجعة</Badge>;
      case 'sent': return <Badge variant="default" className="bg-green-600"><Check className="h-3 w-3 mr-1" />تم الإرسال</Badge>;
      case 'approved': return <Badge variant="default"><Check className="h-3 w-3 mr-1" />معتمد</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  }

  function getFileIcon(filename) {
    const ext = filename?.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return <Image className="h-4 w-4 text-green-500" />;
    if (['pdf'].includes(ext)) return <FileText className="h-4 w-4 text-red-500" />;
    if (['doc', 'docx'].includes(ext)) return <FileText className="h-4 w-4 text-blue-500" />;
    if (['xls', 'xlsx'].includes(ext)) return <FileText className="h-4 w-4 text-green-600" />;
    return <File className="h-4 w-4 text-gray-500" />;
  }

  function formatFileSize(bytes) {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase">المسودات</h1>
          <p className="text-muted-foreground mt-1">Email Drafts - مراجعة وإرسال رسائل البريد</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowCompose(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4 ml-2" />
            إنشاء بريد جديد
          </Button>
          <Button variant="outline" onClick={loadDrafts} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ml-2 ${loading ? 'animate-spin' : ''}`} />
            تحديث
          </Button>
        </div>
      </div>

      {/* Compose New Email Modal */}
      {showCompose && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-blue-600" />
                إنشاء بريد جديد
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowCompose(false)}>
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>إلى (To) *</Label>
                <Input
                  placeholder="example@email.com (أو عدة عناوين مفصولة بفاصلة)"
                  value={newEmail.to}
                  onChange={(e) => setNewEmail({...newEmail, to: e.target.value})}
                  className="mt-1"
                  dir="ltr"
                />
              </div>
              <div>
                <Label>الموضوع (Subject) *</Label>
                <Input
                  placeholder="موضوع البريد"
                  value={newEmail.subject}
                  onChange={(e) => setNewEmail({...newEmail, subject: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>المحتوى (Body)</Label>
                <Textarea
                  placeholder="Dear Eng. Ahmed,

Good Afternoon!
We hope you're doing well.

[اكتب محتوى البريد هنا]

Best regards"
                  value={newEmail.body}
                  onChange={(e) => setNewEmail({...newEmail, body: e.target.value})}
                  className="mt-1 min-h-[200px] font-mono text-sm"
                  dir="ltr"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  💡 اكتب البريد بتنسيق احترافي مع التحية والختام - التوقيع يُضاف تلقائياً
                </p>
              </div>
              
              {/* Attachments Section */}
              <div>
                <Label className="flex items-center gap-2">
                  <Paperclip className="h-4 w-4" />
                  المرفقات (Attachments)
                </Label>
                <div className="mt-2 space-y-2">
                  {newEmail.attachments.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {newEmail.attachments.map((att, idx) => (
                        <div key={idx} className="flex items-center gap-2 px-3 py-2 bg-green-500/10 border border-green-500/20 rounded-lg">
                          <FileText className="h-4 w-4 text-green-600" />
                          <span className="text-sm">{att.name}</span>
                          <span className="text-xs text-muted-foreground">({Math.round(att.size/1024)}KB)</span>
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeAttachment(idx)}>
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      multiple
                      onChange={handleAttachmentUpload}
                      className="hidden"
                      id="attachment-input"
                      accept=".pdf,.docx,.xlsx,.txt,.png,.jpg,.jpeg,.zip"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => document.getElementById('attachment-input').click()}
                      disabled={uploadingAttachment}
                    >
                      {uploadingAttachment ? (
                        <><Loader2 className="h-4 w-4 ml-2 animate-spin" />جاري الرفع...</>
                      ) : (
                        <><Paperclip className="h-4 w-4 ml-2" />إضافة مرفق</>
                      )}
                    </Button>
                    <span className="text-xs text-muted-foreground">PDF, Word, Excel, صور (حتى 10MB)</span>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowCompose(false)}>
                  إلغاء
                </Button>
                <Button 
                  onClick={handleSendNewEmail} 
                  disabled={composing || !newEmail.to || !newEmail.subject}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {composing ? (
                    <>
                      <Loader2 className="h-4 w-4 ml-2 animate-spin" />
                      جاري الإرسال...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 ml-2" />
                      إرسال
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Drafts List */}
        <Card className="bg-card border-border lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileEdit className="h-5 w-5" />المسودات ({drafts.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : drafts.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground px-4">
                <FileEdit className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p>لا توجد مسودات</p>
                <p className="text-sm mt-2">ستظهر هنا مسودات البريد المنشأة من عروض الأسعار</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {drafts.map((draft) => (
                  <div 
                    key={draft.id} 
                    className={`p-4 transition-all cursor-pointer ${
                      selectedDraft?.id === draft.id 
                        ? 'bg-primary/10 border-r-2 border-r-primary' 
                        : 'hover:bg-muted/50'
                    }`}
                    onClick={() => setSelectedDraft(draft)}
                    data-testid={'draft-item-' + draft.id}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          {getStatusBadge(draft.status)}
                          {draft.quotation_no && (
                            <Badge variant="outline" className="font-mono text-xs">
                              {draft.quotation_no}
                            </Badge>
                          )}
                        </div>
                        <h3 className="font-medium text-sm truncate" title={draft.subject}>
                          {draft.subject?.replace('[Nicole] ', '')}
                        </h3>
                        <p className="text-xs text-muted-foreground mt-1 truncate">
                          <Mail className="h-3 w-3 inline mr-1" />
                          {draft.to?.join(', ')}
                        </p>
                        <div className="flex items-center gap-3 mt-1">
                          {draft.total && (
                            <span className="text-xs text-primary font-mono">
                              {draft.total.toLocaleString()} ر.س
                            </span>
                          )}
                          {draft.attachments?.length > 0 && (
                            <span className="text-xs text-muted-foreground">
                              <Paperclip className="h-3 w-3 inline mr-1" />
                              {draft.attachments.length}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          <Calendar className="h-3 w-3 inline mr-1" />
                          {formatDate(draft.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Draft Details & Preview */}
        <Card className="bg-card border-border lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between border-b border-border">
            <CardTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />تفاصيل المسودة
            </CardTitle>
            {selectedDraft && (
              <Button variant="ghost" size="sm" onClick={() => setSelectedDraft(null)}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </CardHeader>
          <CardContent className="p-0">
            {!selectedDraft ? (
              <div className="text-center py-16 text-muted-foreground">
                <Eye className="h-20 w-20 mx-auto mb-4 opacity-30" />
                <p className="text-lg">اختر مسودة لعرض التفاصيل</p>
                <p className="text-sm mt-2">اضغط على أي مسودة من القائمة</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {/* Header Info */}
                <div className="p-4 bg-muted/20">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {getStatusBadge(selectedDraft.status)}
                      {selectedDraft.quotation_no && (
                        <Badge variant="outline" className="font-mono">
                          <Receipt className="h-3 w-3 mr-1" />{selectedDraft.quotation_no}
                        </Badge>
                      )}
                    </div>
                    {selectedDraft.total && (
                      <div className="text-left">
                        <span className="text-2xl font-mono font-bold text-primary">
                          {selectedDraft.total.toLocaleString()}
                        </span>
                        <span className="text-sm text-muted-foreground mr-1">ر.س</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <span className="text-muted-foreground w-16 shrink-0">إلى:</span>
                        <div className="flex flex-wrap gap-1">
                          {selectedDraft.to?.map((email, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">
                              <Mail className="h-3 w-3 mr-1" />{email}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      {selectedDraft.cc?.length > 0 && (
                        <div className="flex items-start gap-2">
                          <span className="text-muted-foreground w-16 shrink-0">نسخة:</span>
                          <div className="flex flex-wrap gap-1">
                            {selectedDraft.cc.map((email, i) => (
                              <Badge key={i} variant="outline" className="text-xs">{email}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      {selectedDraft.customer_name && (
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground w-16 shrink-0">العميل:</span>
                          <span className="font-medium">{selectedDraft.customer_name}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground w-16 shrink-0">التاريخ:</span>
                        <span>{formatDate(selectedDraft.created_at)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-3 pt-3 border-t border-border">
                    <div className="flex items-start gap-2">
                      <span className="text-muted-foreground w-16 shrink-0">الموضوع:</span>
                      <span className="font-medium">{selectedDraft.subject}</span>
                    </div>
                  </div>
                </div>

                {/* Attachments Section */}
                {selectedDraft.attachments?.length > 0 && (
                  <div className="p-4 bg-amber-50/50 dark:bg-amber-950/20">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium flex items-center gap-2">
                        <Paperclip className="h-4 w-4" />
                        المرفقات ({selectedDraft.attachments.length})
                      </h4>
                      <Button 
                        size="sm" 
                        variant="default"
                        onClick={() => setShowAttachments(!showAttachments)}
                        data-testid="view-all-attachments-btn"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        {showAttachments ? 'إخفاء المرفقات' : 'View Attached عرض المرفقات'}
                      </Button>
                    </div>
                    
                    {showAttachments && (
                      <div className="space-y-2">
                        {selectedDraft.attachments.map((attachment, idx) => (
                          <div 
                            key={idx} 
                            className="flex items-center justify-between p-3 bg-white dark:bg-card rounded-sm border border-border"
                          >
                            <div className="flex items-center gap-3">
                              {getFileIcon(attachment.filename || attachment.name)}
                              <div>
                                <p className="font-medium text-sm">{attachment.filename || attachment.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {formatFileSize(attachment.size)}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  if (attachment.url) {
                                    window.open(attachment.url, '_blank');
                                  } else {
                                    toast.info('الملف متاح للإرسال مع البريد');
                                  }
                                }}
                                data-testid={`view-attachment-${idx}`}
                              >
                                <Eye className="h-4 w-4 mr-1" />View
                              </Button>
                              {attachment.download_url && (
                                <Button size="sm" variant="outline" asChild>
                                  <a href={attachment.download_url} download>
                                    <Download className="h-4 w-4 mr-1" />تحميل
                                  </a>
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Email Preview */}
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium flex items-center gap-2">
                      <Mail className="h-4 w-4" />معاينة البريد
                    </h4>
                  </div>
                  <div className="border border-border rounded-sm overflow-hidden">
                    <div 
                      className="p-4 max-h-[400px] overflow-auto bg-white"
                      style={{ minHeight: '200px' }}
                      dangerouslySetInnerHTML={{ __html: selectedDraft.body_en || selectedDraft.body_ar || '<p>لا يوجد محتوى</p>' }}
                    />
                  </div>
                </div>

                {/* Actions */}
                <div className="p-4 bg-muted/20 flex gap-3 justify-end">
                  <Button 
                    variant="outline" 
                    onClick={() => handleDelete(selectedDraft.id)}
                    disabled={deleting === selectedDraft.id}
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  >
                    {deleting === selectedDraft.id ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4 mr-2" />
                    )}
                    حذف المسودة
                  </Button>
                  <Button 
                    onClick={() => handleSend(selectedDraft.id)}
                    disabled={sending === selectedDraft.id || selectedDraft.status === 'sent'}
                    className="min-w-[140px]"
                  >
                    {sending === selectedDraft.id ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : selectedDraft.status === 'sent' ? (
                      <Check className="h-4 w-4 mr-2" />
                    ) : (
                      <Send className="h-4 w-4 mr-2" />
                    )}
                    {selectedDraft.status === 'sent' ? 'تم الإرسال' : 'إرسال البريد'}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default DraftsPage;
